(() => {
    'use strict';

    const qs = (selector, context = document) => context.querySelector(selector);
    const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
    const root = window.STANCECTRL?.routes?.root || '/';

    function createRollingButtonText(label) {
        const roll = document.createElement('span');
        roll.className = 'button-roll';
        const characters = Array.from(label);
        ['button-roll__line--current', 'button-roll__line--next'].forEach((modifier) => {
            const line = document.createElement('span');
            line.className = `button-roll__line ${modifier}`;
            line.setAttribute('aria-hidden', 'true');
            characters.forEach((character, index) => {
                const letter = document.createElement('span');
                letter.className = 'button-roll__letter';
                letter.style.setProperty('--letter-index', index);
                letter.textContent = character === ' ' ? '\u00a0' : character;
                line.appendChild(letter);
            });
            roll.appendChild(line);
        });
        return roll;
    }

    function setRollingButtonLabel(button, label, target = null) {
        const normalizedLabel = String(label || '').replace(/\s+/g, ' ').trim();
        if (!button || !normalizedLabel) return false;
        const roll = createRollingButtonText(normalizedLabel);

        if (target) {
            target.replaceChildren(roll);
        } else {
            const labelNodes = Array.from(button.childNodes).filter((node) => {
                if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim();
                return node.nodeType === Node.ELEMENT_NODE && !node.matches('i, svg');
            });
            if (!labelNodes.length) return false;
            button.insertBefore(roll, labelNodes[0]);
            labelNodes.forEach((node) => node.remove());
        }

        button.classList.add('has-letter-effect');
        if (!button.hasAttribute('aria-label') || button.dataset.letterEffectAria === 'true') {
            button.setAttribute('aria-label', normalizedLabel);
            button.dataset.letterEffectAria = 'true';
        }
        return true;
    }

    function initializeRollingButtons(context = document) {
        const rollingElementSelector = '.button, .desktop-nav > ul > li > a';
        const buttons = [];
        if (context.nodeType === Node.ELEMENT_NODE && context.matches(rollingElementSelector)) buttons.push(context);
        buttons.push(...qsa(rollingElementSelector, context));
        buttons.forEach((button) => {
            if (button.dataset.letterEffectInitialized === 'true' || button.classList.contains('button--no-letter-effect')) return;
            const dynamicLabel = qs('[data-add-to-cart-text]', button);
            if (setRollingButtonLabel(button, dynamicLabel?.textContent || button.textContent, dynamicLabel)) {
                button.dataset.letterEffectInitialized = 'true';
            }
        });
    }

    function initializeRandomProductGrids(context = document) {
        const grids = [];
        if (context.nodeType === Node.ELEMENT_NODE && context.matches('[data-random-products]')) grids.push(context);
        grids.push(...qsa('[data-random-products]', context));
        grids.forEach((grid) => {
            if (grid.dataset.randomProductsInitialized === 'true') return;
            const cards = qsa('.random-product-card', grid);
            const count = Math.min(Number.parseInt(grid.dataset.randomProductCount, 10) || 5, cards.length);
            const shuffledCards = [...cards];
            for (let index = shuffledCards.length - 1; index > 0; index -= 1) {
                const randomIndex = Math.floor(Math.random() * (index + 1));
                [shuffledCards[index], shuffledCards[randomIndex]] = [shuffledCards[randomIndex], shuffledCards[index]];
            }
            cards.forEach((card) => card.classList.remove('is-random-visible'));
            shuffledCards.slice(0, count).forEach((card) => card.classList.add('is-random-visible'));
            grid.dataset.randomProductsInitialized = 'true';
        });
    }

    function initializeAccordions(context = document) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const accordions = [];
        if (context.nodeType === Node.ELEMENT_NODE && context.matches('.accordion')) accordions.push(context);
        accordions.push(...qsa('.accordion', context));
        accordions.forEach((accordion) => {
            if (accordion.dataset.accordionInitialized === 'true') return;
            const summary = qs('summary', accordion);
            const content = qs('.accordion__content', accordion);
            if (!summary || !content) return;
            accordion.dataset.accordionInitialized = 'true';

            summary.addEventListener('click', (event) => {
                event.preventDefault();
                const isClosing = accordion.dataset.accordionState === 'closing';
                const shouldOpen = !accordion.open || isClosing;
                const currentHeight = accordion.getBoundingClientRect().height;
                const borderHeight = accordion.offsetHeight - accordion.clientHeight;
                accordion._accordionAnimation?.cancel();
                accordion._accordionContentAnimation?.cancel();
                accordion.style.overflow = 'hidden';

                if (shouldOpen) {
                    accordion.open = true;
                    accordion.dataset.accordionState = 'opening';
                    const endHeight = accordion.scrollHeight + borderHeight;
                    const animation = accordion.animate([
                        {height: `${currentHeight}px`},
                        {height: `${endHeight}px`}
                    ], {duration: 460, easing: 'cubic-bezier(.16, 1, .3, 1)'});
                    accordion._accordionContentAnimation = content.animate([
                        {opacity: 0, transform: 'translateY(-4px)'},
                        {opacity: 1, transform: 'translateY(0)'}
                    ], {duration: 320, delay: 45, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both'});
                    accordion._accordionAnimation = animation;
                    animation.onfinish = () => {
                        accordion.dataset.accordionState = 'open';
                        accordion.style.overflow = '';
                        accordion._accordionAnimation = null;
                        accordion._accordionContentAnimation?.cancel();
                        accordion._accordionContentAnimation = null;
                    };
                } else {
                    accordion.dataset.accordionState = 'closing';
                    const endHeight = summary.offsetHeight + borderHeight;
                    const animation = accordion.animate([
                        {height: `${currentHeight}px`},
                        {height: `${endHeight}px`}
                    ], {duration: 380, easing: 'cubic-bezier(.4, 0, .2, 1)'});
                    accordion._accordionContentAnimation = content.animate([
                        {opacity: 1, transform: 'translateY(0)'},
                        {opacity: 0, transform: 'translateY(-3px)'}
                    ], {duration: 220, easing: 'ease-out', fill: 'both'});
                    accordion._accordionAnimation = animation;
                    animation.onfinish = () => {
                        accordion.open = false;
                        accordion.dataset.accordionState = 'closed';
                        accordion.style.overflow = '';
                        accordion._accordionAnimation = null;
                        accordion._accordionContentAnimation?.cancel();
                        accordion._accordionContentAnimation = null;
                    };
                }
            });
        });
    }

    function initializeProductSliceEffects(context = document) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const mediaAreas = [];
        if (context.nodeType === Node.ELEMENT_NODE && context.matches('[data-product-slice-media]')) mediaAreas.push(context);
        mediaAreas.push(...qsa('[data-product-slice-media]', context));
        mediaAreas.forEach((media) => {
            if (media.dataset.productSliceInitialized === 'true') return;
            const effect = qs('[data-product-slice-effect]', media);
            const slices = qsa('[data-product-slice]', media);
            if (!effect || slices.length < 2) return;
            media.dataset.productSliceInitialized = 'true';

            const state = {
                x: 0,
                y: 0,
                intensity: 0,
                targetX: 0,
                targetY: 0,
                targetIntensity: 0,
                animationFrame: null
            };

            const render = () => {
                if (!media.isConnected) return;
                state.x += (state.targetX - state.x) * 0.105;
                state.y += (state.targetY - state.y) * 0.105;
                state.intensity += (state.targetIntensity - state.intensity) * 0.09;

                slices.forEach((slice, index) => {
                    const progress = index / Math.max(1, slices.length - 1);
                    const edgeDistance = Math.abs(progress - 0.5) * 2;
                    const direction = index % 2 === 0 ? 1 : -1;
                    const sliceX = (state.x * (4.5 + edgeDistance * 4) * direction + state.y * (progress - 0.5) * 2.5) * state.intensity;
                    const sliceY = (state.y * (1.25 + edgeDistance * 1.8) + state.x * direction * 0.55) * state.intensity;
                    const skew = state.x * direction * (0.28 + edgeDistance * 0.3) * state.intensity;
                    const scaleY = 1 + Math.abs(state.y) * (0.008 + edgeDistance * 0.007) * state.intensity;
                    const variance = 0.55 + ((index * 3) % 7) / 10;
                    const reveal = Math.min(30, (Math.abs(state.x) * 18 + Math.abs(state.y) * 7) * variance * state.intensity);
                    const revealFromRight = state.x * direction >= 0;

                    slice.style.setProperty('--slice-x', `${sliceX.toFixed(3)}px`);
                    slice.style.setProperty('--slice-y', `${sliceY.toFixed(3)}px`);
                    slice.style.setProperty('--slice-skew', `${skew.toFixed(3)}deg`);
                    slice.style.setProperty('--slice-scale-y', scaleY.toFixed(4));
                    slice.style.setProperty('--reveal-left', `${revealFromRight ? 0 : reveal.toFixed(2)}%`);
                    slice.style.setProperty('--reveal-right', `${revealFromRight ? reveal.toFixed(2) : 0}%`);
                    slice.style.setProperty('--secondary-x', `${(-sliceX * 0.18).toFixed(3)}px`);
                    slice.style.setProperty('--secondary-y', `${(-sliceY * 0.16).toFixed(3)}px`);
                    slice.style.setProperty('--primary-x', `${(sliceX * 0.06).toFixed(3)}px`);
                    slice.style.setProperty('--primary-y', `${(sliceY * 0.04).toFixed(3)}px`);
                });

                const remainingMotion = Math.abs(state.targetX - state.x) + Math.abs(state.targetY - state.y) + Math.abs(state.targetIntensity - state.intensity);
                if (remainingMotion > 0.002) state.animationFrame = requestAnimationFrame(render);
                else state.animationFrame = null;
            };

            const requestRender = () => {
                if (state.animationFrame === null) state.animationFrame = requestAnimationFrame(render);
            };

            media.addEventListener('pointermove', (event) => {
                if (event.pointerType === 'touch') return;
                const bounds = media.getBoundingClientRect();
                state.targetX = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width - 0.5) * 2));
                state.targetY = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height - 0.5) * 2));
                state.targetIntensity = 1;
                requestRender();
            });
            media.addEventListener('pointerleave', () => {
                state.targetX = 0;
                state.targetY = 0;
                state.targetIntensity = 0;
                requestRender();
            });

            const preloadImage = (url) => new Promise((resolve) => {
                const image = new Image();
                image.onload = resolve;
                image.onerror = resolve;
                image.src = url;
            });
            Promise.all([
                preloadImage(effect.dataset.primaryImage),
                preloadImage(effect.dataset.secondaryImage)
            ]).then(() => {
                if (media.isConnected) media.classList.add('is-slice-ready');
            });
        });
    }

    initializeRollingButtons();
    initializeRandomProductGrids();
    initializeAccordions();
    initializeProductSliceEffects();
    const buttonObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                initializeRollingButtons(node);
                initializeRandomProductGrids(node);
                initializeAccordions(node);
                initializeProductSliceEffects(node);
            }
        }));
    });
    buttonObserver.observe(document.body, {childList: true, subtree: true});
    document.addEventListener('shopify:section:load', (event) => {
        initializeRollingButtons(event.target);
        initializeRandomProductGrids(event.target);
        initializeAccordions(event.target);
        initializeProductSliceEffects(event.target);
    });
    document.addEventListener('shopify:block:select', (event) => initializeRollingButtons(event.target));
    window.addEventListener('pageshow', () => {
        initializeRollingButtons();
        initializeRandomProductGrids();
        initializeAccordions();
        initializeProductSliceEffects();
    });

    function updateThemeControls(theme) {
        qsa('[data-theme-toggle]').forEach((button) => {
            const isLight = theme === 'light';
            button.setAttribute('aria-pressed', String(isLight));
            button.setAttribute('aria-label', `Switch to ${isLight ? 'dark' : 'light'} theme`);
            const label = qs('[data-theme-label]', button);
            if (label) label.textContent = `${isLight ? 'DARK' : 'LIGHT'} MODE`;
        });
        const themeColor = qs('meta[name="theme-color"]');
        if (themeColor) themeColor.content = theme === 'light' ? '#f5f5f7' : '#080808';
    }

    function setTheme(theme) {
        const nextTheme = theme === 'light' ? 'light' : 'dark';
        document.documentElement.dataset.theme = nextTheme;
        try {
            localStorage.setItem('stancectrl-theme', nextTheme);
        } catch (error) { /* Storage can be disabled. */
        }
        updateThemeControls(nextTheme);
    }

    updateThemeControls(document.documentElement.dataset.theme || 'dark');
    document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-theme-toggle]');
        if (!toggle) return;
        setTheme(document.documentElement.dataset.theme === 'light' ? 'dark' : 'light');
    });

    window.addEventListener('storage', (event) => {
        if (event.key === 'stancectrl-theme' && event.newValue) {
            document.documentElement.dataset.theme = event.newValue === 'light' ? 'light' : 'dark';
            updateThemeControls(document.documentElement.dataset.theme);
        }
    });

    function formatMoney(cents, format = window.STANCECTRL?.moneyFormat || '${{amount}}') {
        const value = Number(cents || 0);
        const amount = (value / 100).toFixed(2);
        const amountNoDecimals = Math.round(value / 100).toString();
        const formatted = format
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
            .replace(/\{\{\s*amount\s*\}\}/g, amount);
        return formatted;
    }

    function lockScroll(lock) {
        document.documentElement.classList.toggle('is-locked', lock);
    }

    const overlay = qs('[data-drawer-overlay]');
    const cartDrawer = qs('#CartDrawer');
    const mobileMenu = qs('#MobileMenu');
    const siteHeader = qs('[data-site-header]');

    if (siteHeader) {
        let headerTicking = false;
        const updateHeaderState = () => {
            siteHeader.classList.toggle('is-scrolled', window.scrollY > 24);
            headerTicking = false;
        };

        updateHeaderState();
        window.addEventListener('scroll', () => {
            if (headerTicking) return;
            headerTicking = true;
            requestAnimationFrame(updateHeaderState);
        }, {passive: true});
    }

    function openLayer(layer) {
        if (!layer) return;
        layer.hidden = false;
        requestAnimationFrame(() => layer.classList.add('is-open'));
        if (overlay) {
            overlay.hidden = false;
            requestAnimationFrame(() => overlay.classList.add('is-open'));
        }
        lockScroll(true);
    }

    function closeLayer(layer) {
        if (!layer) return;
        layer.classList.remove('is-open');
        if (overlay) overlay.classList.remove('is-open');
        setTimeout(() => {
            layer.hidden = true;
            if (overlay) overlay.hidden = true;
        }, 250);
        lockScroll(false);
    }

    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', () => openLayer(cartDrawer)));
    qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', () => closeLayer(cartDrawer)));
    qsa('[data-mobile-menu-open]').forEach((button) => button.addEventListener('click', () => openLayer(mobileMenu)));
    qsa('[data-mobile-menu-close]').forEach((button) => button.addEventListener('click', () => closeLayer(mobileMenu)));
    overlay?.addEventListener('click', () => {
        if (cartDrawer && !cartDrawer.hidden) closeLayer(cartDrawer);
        if (mobileMenu && !mobileMenu.hidden) closeLayer(mobileMenu);
    });

    const searchDialog = qs('[data-search-dialog]');
    qsa('[data-search-open]').forEach((button) => button.addEventListener('click', () => {
        if (!searchDialog) return;
        searchDialog.showModal();
        qs('input[type="search"]', searchDialog)?.focus();
    }));
    qsa('[data-search-close]').forEach((button) => button.addEventListener('click', () => searchDialog?.close()));

    async function getCart() {
        const response = await fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}});
        if (!response.ok) throw new Error('Unable to load cart');
        return response.json();
    }

    function escapeHtml(value = '') {
        return String(value).replace(/[&<>'"]/g, (char) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            "'": '&#39;',
            '"': '&quot;'
        }[char]));
    }

    function renderCart(cart) {
        qsa('[data-cart-count]').forEach((node) => {
            node.textContent = cart.item_count;
            node.classList.toggle('is-empty', cart.item_count === 0);
        });
        const body = qs('[data-cart-drawer-body]');
        const footer = qs('[data-cart-drawer-footer]');
        if (!body || !footer) return;

        if (!cart.items.length) {
            body.innerHTML = `<div class="empty-state"><p>Your cart is empty.</p><a href="${root}collections/all" class="button button--signal">Continue shopping</a></div>`;
            footer.hidden = true;
            return;
        }

        body.innerHTML = `<div class="cart-lines" data-cart-lines>${cart.items.map((item) => `
      <article class="cart-line" data-line-key="${escapeHtml(item.key)}">
        <a href="${escapeHtml(item.url)}" class="cart-line__image">${item.image ? `<img src="${escapeHtml(item.image)}&width=240" alt="${escapeHtml(item.product_title)}" loading="lazy">` : ''}</a>
        <div class="cart-line__content">
          <a href="${escapeHtml(item.url)}" class="cart-line__title">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHtml(item.variant_title)}</p>` : ''}
          ${Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_')).map(([key, value]) => `<p>${escapeHtml(key)}: ${escapeHtml(value)}</p>`).join('')}
          <p>${formatMoney(item.final_line_price)}</p>
          <div class="quantity-control">
            <button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button type="button" class="cart-line__remove" data-cart-remove>Remove</button>
      </article>`).join('')}</div>`;
        qs('[data-cart-total]')?.replaceChildren(document.createTextNode(formatMoney(cart.total_price)));
        footer.hidden = false;
        bindCartLineActions();
    }

    async function changeCartLine(key, quantity) {
        const response = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({id: key, quantity})
        });
        if (!response.ok) throw new Error('Unable to update cart');
        const cart = await response.json();
        renderCart(cart);
    }

    function bindCartLineActions() {
        qsa('[data-line-key]', cartDrawer).forEach((line) => {
            const key = line.dataset.lineKey;
            qsa('[data-cart-quantity]', line).forEach((button) => button.addEventListener('click', () => changeCartLine(key, Number(button.dataset.cartQuantity)).catch(console.error)));
            qs('[data-cart-remove]', line)?.addEventListener('click', () => changeCartLine(key, 0).catch(console.error));
        });
    }

    bindCartLineActions();

    qsa('[data-ajax-product-form]').forEach((form) => {
        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            const submit = qs('[type="submit"]', form);
            const status = qs('[data-product-status]', form.closest('[data-product-root]') || form.parentElement);
            submit?.setAttribute('aria-busy', 'true');
            if (submit) submit.disabled = true;
            if (status) status.textContent = '';
            try {
                const response = await fetch(`${root}cart/add.js`, {
                    method: 'POST',
                    headers: {Accept: 'application/json'},
                    body: new FormData(form)
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.description || 'Unable to add item');
                const cart = await getCart();
                renderCart(cart);
                openLayer(cartDrawer);
            } catch (error) {
                if (status) status.textContent = error.message;
                else alert(error.message);
            } finally {
                submit?.removeAttribute('aria-busy');
                if (submit) submit.disabled = false;
            }
        });
    });

    qsa('[data-product-gallery]').forEach((gallery) => {
        const slides = qsa('[data-slide-index]', gallery);
        if (!slides.length) return;
        let currentIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
        let touchStartX = 0;

        const showSlide = (index) => {
            currentIndex = (index + slides.length) % slides.length;
            slides.forEach((slide, slideIndex) => {
                const isActive = slideIndex === currentIndex;
                slide.hidden = !isActive;
                slide.setAttribute('aria-hidden', String(!isActive));
                if (!isActive) qsa('video', slide).forEach((video) => video.pause());
            });
            const counter = qs('[data-carousel-current]', gallery);
            if (counter) counter.textContent = currentIndex + 1;
            qsa('[data-carousel-thumbnail]', gallery).forEach((thumbnail, thumbnailIndex) => {
                const isActive = thumbnailIndex === currentIndex;
                thumbnail.classList.toggle('is-active', isActive);
                if (isActive) thumbnail.setAttribute('aria-current', 'true');
                else thumbnail.removeAttribute('aria-current');
            });
        };

        gallery.showMediaById = (mediaId) => {
            const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
            if (index >= 0) showSlide(index);
        };

        qs('[data-carousel-previous]', gallery)?.addEventListener('click', () => showSlide(currentIndex - 1));
        qs('[data-carousel-next]', gallery)?.addEventListener('click', () => showSlide(currentIndex + 1));
        qsa('[data-carousel-thumbnail]', gallery).forEach((thumbnail) => {
            thumbnail.addEventListener('click', () => showSlide(Number(thumbnail.dataset.carouselThumbnail)));
        });
        gallery.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            showSlide(currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
        });
        gallery.addEventListener('touchstart', (event) => {
            touchStartX = event.changedTouches[0].clientX;
        }, {passive: true});
        gallery.addEventListener('touchend', (event) => {
            const distance = event.changedTouches[0].clientX - touchStartX;
            if (Math.abs(distance) > 45) showSlide(currentIndex + (distance < 0 ? 1 : -1));
        }, {passive: true});
        showSlide(currentIndex);
    });

    qsa('[data-product-root]').forEach((productRoot) => {
        const select = qs('[data-variant-select]', productRoot);
        const variantsScript = qs('[data-product-variants]', productRoot);
        let variants = [];
        if (variantsScript) {
            try {
                variants = JSON.parse(variantsScript.textContent);
            } catch (error) {
                console.error(error);
            }
        }
        const sizeButtons = qsa('[data-size-value]', productRoot);
        const sizeProperty = qs('[data-size-property]', productRoot);
        const sizeList = qs('[data-size-options]', productRoot);
        const normalizeSize = (value) => String(value || '').trim().toUpperCase().replace(/[\s_-]+/g, '');
        const sizeRanks = new Map([
            ['XXXS', 0], ['3XS', 0], ['XXS', 1], ['2XS', 1], ['XS', 2],
            ['S', 3], ['SMALL', 3], ['M', 4], ['MEDIUM', 4], ['L', 5], ['LARGE', 5],
            ['XL', 6], ['1XL', 6], ['XXL', 7], ['2XL', 7], ['XXXL', 8], ['3XL', 8],
            ['4XL', 9], ['5XL', 10], ['6XL', 11]
        ]);
        const getSizeRank = (value) => {
            const normalized = normalizeSize(value);
            if (sizeRanks.has(normalized)) return sizeRanks.get(normalized);
            const numericSize = Number.parseFloat(normalized);
            return Number.isNaN(numericSize) ? 1000 : 100 + numericSize;
        };
        if (sizeList) {
            [...sizeButtons]
                .sort((first, second) => getSizeRank(first.dataset.sizeValue) - getSizeRank(second.dataset.sizeValue))
                .forEach((button) => sizeList.appendChild(button));
        }
        const markSelectedSize = (value) => {
            sizeButtons.forEach((button) => {
                const isSelected = button.dataset.sizeValue === String(value);
                button.classList.toggle('is-selected', isSelected);
                button.setAttribute('aria-pressed', String(isSelected));
            });
        };
        sizeButtons.forEach((sizeButton) => sizeButton.addEventListener('click', () => {
            const sizeValue = sizeButton.dataset.sizeValue;
            const sizeOptionIndex = Number.parseInt(sizeButton.dataset.sizeOptionIndex, 10);
            if (select && !Number.isNaN(sizeOptionIndex) && variants.length) {
                const currentVariant = variants.find((variant) => String(variant.id) === select.value) || variants[0];
                const desiredOptions = [...(currentVariant.options || [])];
                desiredOptions[sizeOptionIndex] = sizeValue;
                const nextVariant = variants.find((variant) => variant.available && variant.options.every((option, index) => option === desiredOptions[index]))
                    || variants.find((variant) => variant.available && variant.options[sizeOptionIndex] === sizeValue);
                if (!nextVariant) return;
                select.value = String(nextVariant.id);
                select.dispatchEvent(new Event('change', {bubbles: true}));
            } else {
                if (sizeProperty) sizeProperty.value = sizeValue;
                markSelectedSize(sizeValue);
            }
        }));

        if (!select || !variantsScript) return;
        select.addEventListener('change', () => {
            const variant = variants.find((item) => String(item.id) === select.value);
            if (!variant) return;
            const sizeButton = sizeButtons.find((button) => !Number.isNaN(Number.parseInt(button.dataset.sizeOptionIndex, 10)));
            if (sizeButton) markSelectedSize(variant.options[Number.parseInt(sizeButton.dataset.sizeOptionIndex, 10)]);
            const price = qs('[data-product-price]', productRoot);
            const button = qs('[data-add-to-cart]', productRoot);
            const buttonText = qs('[data-add-to-cart-text]', productRoot);
            const availability = qs('[data-product-availability]', productRoot);
            const availabilityText = qs('[data-product-availability-text]', productRoot);
            if (price) price.textContent = formatMoney(variant.price);
            if (button) button.disabled = !variant.available;
            if (buttonText) {
                const label = variant.available ? 'Add to cart' : 'Sold out';
                if (button?.classList.contains('has-letter-effect')) setRollingButtonLabel(button, label, buttonText);
                else buttonText.textContent = label;
            }
            availability?.classList.toggle('is-unavailable', !variant.available);
            if (availabilityText) availabilityText.textContent = variant.available ? 'In stock, ready to ship' : 'Out of stock';
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url);
            if (variant.featured_media?.id) {
                const media = qs(`[data-media-id="${variant.featured_media.id}"]`, productRoot);
                const gallery = media?.closest('[data-product-gallery]');
                if (gallery?.showMediaById) gallery.showMediaById(variant.featured_media.id);
            }
        });
    });

    qsa('[data-quantity-stepper]').forEach((stepper) => {
        const input = qs('input[type="number"]', stepper);
        if (!input) return;
        qs('[data-quantity-minus]', stepper)?.addEventListener('click', () => {
            const minimum = Number(input.min || 1);
            input.value = Math.max(minimum, Number(input.value || minimum) - 1);
            input.dispatchEvent(new Event('change', {bubbles: true}));
        });
        qs('[data-quantity-plus]', stepper)?.addEventListener('click', () => {
            const maximum = input.max ? Number(input.max) : Infinity;
            input.value = Math.min(maximum, Number(input.value || input.min || 1) + 1);
            input.dispatchEvent(new Event('change', {bubbles: true}));
        });
    });

    qsa('[data-filter-toggle]').forEach((button) => button.addEventListener('click', () => qs('[data-filter-panel]', button.parentElement)?.classList.toggle('is-open')));
    qsa('.collection-controls select[name="sort_by"]').forEach((select) => select.addEventListener('change', () => select.form.submit()));

    const collectionFilterDrawer = qs('[data-collection-filter-drawer]');
    const collectionFilterTrigger = qs('[data-collection-filter-open]');
    const collectionFilterClose = qs('[data-collection-filter-close]');
    const collectionFilterOverlay = qs('[data-collection-filter-overlay]');
    let collectionFilterPreviousFocus = null;

    function openCollectionFilters() {
        if (!collectionFilterDrawer || !collectionFilterOverlay) return;
        collectionFilterPreviousFocus = document.activeElement;
        collectionFilterOverlay.hidden = false;
        requestAnimationFrame(() => {
            collectionFilterDrawer.classList.add('is-open');
            collectionFilterOverlay.classList.add('is-open');
        });
        collectionFilterDrawer.setAttribute('aria-hidden', 'false');
        collectionFilterDrawer.removeAttribute('inert');
        collectionFilterTrigger?.setAttribute('aria-expanded', 'true');
        lockScroll(true);
        setTimeout(() => collectionFilterClose?.focus(), 80);
    }

    function closeCollectionFilters({restoreFocus = true} = {}) {
        if (!collectionFilterDrawer || !collectionFilterOverlay) return;
        collectionFilterDrawer.classList.remove('is-open');
        collectionFilterOverlay.classList.remove('is-open');
        collectionFilterDrawer.setAttribute('aria-hidden', 'true');
        collectionFilterDrawer.setAttribute('inert', '');
        collectionFilterTrigger?.setAttribute('aria-expanded', 'false');
        lockScroll(false);
        setTimeout(() => {
            if (!collectionFilterDrawer.classList.contains('is-open')) collectionFilterOverlay.hidden = true;
        }, 420);
        if (restoreFocus && collectionFilterPreviousFocus instanceof HTMLElement) collectionFilterPreviousFocus.focus();
    }

    collectionFilterTrigger?.addEventListener('click', openCollectionFilters);
    collectionFilterClose?.addEventListener('click', () => closeCollectionFilters());
    collectionFilterOverlay?.addEventListener('click', () => closeCollectionFilters());
    collectionFilterDrawer?.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = qsa('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled])', collectionFilterDrawer)
            .filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
        });
    }, {threshold: 0.08}) : null;
    qsa('.reveal').forEach((element) => revealObserver ? revealObserver.observe(element) : element.classList.add('is-visible'));

    document.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        if (cartDrawer && !cartDrawer.hidden) closeLayer(cartDrawer);
        if (mobileMenu && !mobileMenu.hidden) closeLayer(mobileMenu);
        if (collectionFilterDrawer?.classList.contains('is-open')) closeCollectionFilters();
    });
})();
